'use client';

import { Fragment } from 'react';
import { RadialBarChart, RadialBar, PolarAngleAxis, PolarGrid } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../chart';

export interface RadialShapeItem {
  key: string;
  label: string;
  value: number;
  color: string;
}

interface RadialShapeChartProps {
  data: RadialShapeItem[];
  showLegend?: boolean;
  height?: number;
  className?: string;
}

export function RadialShapeChart({
  data,
  showLegend = true,
  height = 220,
  className,
}: RadialShapeChartProps) {
  const config: ChartConfig = Object.fromEntries(
    data.map((d) => [d.key, { label: d.label, color: d.color }]),
  );

  const chartData = data.map((d) => ({ ...d, fill: d.color }));

  const LegendContent = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '4px 16px',
        paddingTop: 8,
      }}
    >
      {data.map((item) => (
        <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: 2,
              background: item.color,
              flexShrink: 0,
            }}
          />
          <span className="text-[var(--color-text-secondary,#71717a)]">{item.label}</span>
          <span className="font-bold text-[var(--color-text-primary,#18181b)]">{item.value}</span>
        </div>
      ))}
    </div>
  );

  return (
    <Fragment>
      <ChartContainer config={config} className={className} style={{ height }}>
        <RadialBarChart
          data={chartData}
          innerRadius="20%"
          outerRadius="90%"
          startAngle={90}
          endAngle={-270}
        >
          <PolarGrid gridType="circle" radialLines={false} stroke="none" />
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar dataKey="value" background cornerRadius={6} />
          <ChartTooltip content={<ChartTooltipContent nameKey="key" />} />
        </RadialBarChart>
      </ChartContainer>
      {showLegend && <LegendContent />}
    </Fragment>
  );
}
